/**
 * Machine credentials.
 *
 * The rotation use case shipped in M0 with **no caller anywhere** — no endpoint, no CLI
 * command, no test that ran it. A tenant could not obtain an API key through any supported
 * interface, and nothing noticed because the table, the repository and the authentication
 * path were all present and correct.
 *
 * Two things this screen is careful about:
 *
 * * **The key is shown once.** Only its hash is stored, so there is no "reveal" to build.
 *   The list shows the prefix, which identifies a key in a log line and authenticates
 *   nothing.
 * * **Revoked keys stay listed.** "Was a key ever issued for that integration?" is a
 *   question an operator asks after the fact, and a list that hid withdrawn keys would
 *   make it unanswerable.
 */

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { ApiKeyResponse, CreateApiKeyRequest, MintedApiKey } from '@/lib/schemas';
import { Badge, Button, Card, ErrorState, Loading, Table } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

/** The API key registry. */
export function ApiKeysPage(): ReactElement {
  const canManage = useCan('manage_credentials');
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [minted, setMinted] = useState<MintedApiKey | null>(null);

  const keys = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => request<ApiKeyResponse[]>('/api-keys'),
    enabled: canManage,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
  };

  const create = useMutation({
    mutationFn: () =>
      request<MintedApiKey, CreateApiKeyRequest>('/api-keys', { method: 'POST', body: { name, scopes: [] } }),
    onSuccess: (result) => {
      setMinted(result);
      setName('');
      refresh();
    },
  });

  const rotate = useMutation({
    mutationFn: (key: ApiKeyResponse) =>
      request<MintedApiKey, CreateApiKeyRequest>(`/api-keys/${key.id}:rotate`, {
        method: 'POST',
        body: { name: key.name, scopes: key.scopes },
      }),
    onSuccess: (result) => {
      setMinted(result);
      refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: (keyId: string) =>
      request<ApiKeyResponse>(`/api-keys/${keyId}:revoke`, { method: 'POST' }),
    onSuccess: refresh,
  });

  if (!canManage) {
    return (
      <p className="text-sm text-muted">
        Managing API keys requires the <code>manage_credentials</code> permission, which your
        role does not hold.
      </p>
    );
  }
  if (keys.isLoading) return <Loading />;
  if (keys.error) return <ErrorState error={keys.error} />;

  const failure = create.error ?? rotate.error ?? revoke.error;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">API keys</h1>
      <p className="text-sm text-muted">
        Machine principals. A key can read and collect; it can never approve a merge, confirm
        a hypothesis or approve an export — those require a person, enforced in the
        authorisation layer rather than by convention.
      </p>

      {failure ? <ErrorState error={failure} /> : null}

      {minted ? (
        <Card title="Copy this key now">
          <div className="space-y-2 p-4">
            <p className="text-xs text-muted">
              Shown once. Only its hash is stored, so it cannot be retrieved again — mint a
              replacement if you lose it.
            </p>
            <code
              data-testid="minted-api-key"
              className="block break-all rounded bg-base px-2 py-1 font-mono text-xs"
            >
              {minted.presented_key}
            </code>
            <Button onClick={() => setMinted(null)}>I have copied it</Button>
          </div>
        </Card>
      ) : null}

      <Card title="Mint a key">
        <div className="flex flex-wrap items-end gap-2 p-4">
          <label className="flex flex-col text-xs">
            Label
            <input
              className="rounded border border-line bg-raised px-2 py-1 text-xs"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="ci-pipeline"
            />
          </label>
          <Button onClick={() => create.mutate()} disabled={!name || create.isPending}>
            Create
          </Button>
        </div>
      </Card>

      <Table
        headers={['Label', 'Prefix', 'Created', 'Last used', 'Status', '']}
        empty="No API keys have been issued."
        rows={(keys.data ?? []).map((key) => [
          key.name,
          <code key="p" className="font-mono text-xs">
            {key.prefix}
          </code>,
          <span key="c" className="text-xs text-muted">
            {new Date(key.created_at).toLocaleDateString()}
          </span>,
          <span key="u" className="text-xs text-muted">
            {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'never'}
          </span>,
          <Badge key="s" tone={key.revoked_at ? 'muted' : 'ok'}>
            {key.revoked_at ? 'revoked' : 'active'}
          </Badge>,
          key.revoked_at ? (
            <span key="a" />
          ) : (
            <span key="a" className="flex gap-2">
              <Button onClick={() => rotate.mutate(key)} disabled={rotate.isPending}>
                Rotate
              </Button>
              <Button onClick={() => revoke.mutate(key.id)} disabled={revoke.isPending}>
                Revoke
              </Button>
            </span>
          ),
        ])}
      />
    </div>
  );
}
